// Thematic map coloring — a min→max stretch over a perceptually uniform
// ramp. Deliberately does NOT reuse `resultsAnimationColors.ts`'s `heatColor`
// (blue→cyan→green→yellow→red): that ramp exists to match the real SWMM
// GUI's default "Rainbow" legend one-for-one for results playback, but for
// a static attribute map that same rainbow buys nothing (there's no SWMM
// desktop file compatibility on the line here) and inherits every well-
// known problem with rainbow ramps in technical visualization: it isn't
// perceptually uniform (equal steps in the data don't look equal on
// screen), the middle bright yellow reads brighter than either endpoint so
// ordinality is lost, and it fails several colorblind simulations. Viridis
// avoids all of those — monotone luminance, colorblind-safe, published
// alongside matplotlib as a designed replacement for exactly this ramp.
//
// Every current `THEMATIC_ATTRIBUTES` entry (see model/inp/thematicAttributes.ts)
// is a magnitude — diameter, area, elevation, roughness, %impervious, max
// depth, max flow — none is a signed difference. So a single sequential
// ramp is the correct fit here; a diverging RdBu/PuOr would only make
// sense if a future attribute compared two scenarios or a signed delta.

interface Stop {
  ratio: number;
  r: number;
  g: number;
  b: number;
}

// Canonical viridis 5-anchor sample from matplotlib's own 256-entry lookup
// table, taken at indices 0 / 64 / 128 / 191 / 255. Linearly interpolated
// between these anchors matches the full matplotlib ramp to within a few
// units per channel — plenty for a screen legend.
const VIRIDIS_STOPS: Stop[] = [
  { ratio: 0, r: 68, g: 1, b: 84 },
  { ratio: 0.25, r: 59, g: 82, b: 139 },
  { ratio: 0.5, r: 33, g: 145, b: 140 },
  { ratio: 0.75, r: 94, g: 201, b: 98 },
  { ratio: 1, r: 253, g: 231, b: 37 },
];

/** Clamps ratio to 0..1 so floating-point noise slightly past the exact endpoints still renders sensibly. */
export function viridisColor(ratio: number): string {
  const t = Math.max(0, Math.min(1, ratio));
  let lo = VIRIDIS_STOPS[0];
  let hi = VIRIDIS_STOPS[VIRIDIS_STOPS.length - 1];
  for (let i = 0; i < VIRIDIS_STOPS.length - 1; i++) {
    if (t >= VIRIDIS_STOPS[i].ratio && t <= VIRIDIS_STOPS[i + 1].ratio) {
      lo = VIRIDIS_STOPS[i];
      hi = VIRIDIS_STOPS[i + 1];
      break;
    }
  }
  const span = hi.ratio - lo.ratio;
  const f = span > 0 ? (t - lo.ratio) / span : 0;
  const r = Math.round(lo.r + (hi.r - lo.r) * f);
  const g = Math.round(lo.g + (hi.g - lo.g) * f);
  const b = Math.round(lo.b + (hi.b - lo.b) * f);
  return `rgb(${r}, ${g}, ${b})`;
}

interface ThematicColorResult {
  colors: Map<string, string>;
  min: number;
  max: number;
}

export function buildThematicColors(values: Map<string, number>): ThematicColorResult {
  if (values.size === 0) return { colors: new Map(), min: 0, max: 0 };
  let min = Infinity;
  let max = -Infinity;
  for (const v of values.values()) {
    if (v < min) min = v;
    if (v > max) max = v;
  }
  const span = max - min;
  const colors = new Map<string, string>();
  for (const [name, v] of values) colors.set(name, viridisColor(span > 0 ? (v - min) / span : 0));
  return { colors, min, max };
}
